/**
 * Small pure helpers shared across the operations-dashboard page and its
 * stat-card/column pieces: period-over-period percent change, the verbose
 * trend text the New/Resolved Alarms cards show over a week-or-longer
 * window, the abbreviated site-hours label used in stat-card helper text,
 * the maintenance-tab client-side text search, and the maintenance-tab
 * time-window filter.
 *
 * Contains:
 * - calcChange — period-over-period percent change (rounded, Infinity/0 edge cases)
 * - formatTrendLabel — "Up 12%" / "Down 8%" / "No change" / "New" from a calcChange result
 * - formatTrendPrevious — "was {{value}}" caption for a previous-period value
 * - formatSiteHours — abbreviated hour count (e.g. "1.2k h")
 * - matchesSearch — case-insensitive substring match across a row's grid columns
 * - filterMaintenanceByWindow — windows maintenance rows to the active hours lookback
 *
 * Returns plain JS — no JSX.
 */

export function calcChange(current, previous) {
  if (previous == null || current == null) return null;
  if (previous === 0) return current > 0 ? Infinity : 0;
  return Math.round(((current - previous) / previous) * 100);
}

/**
 * @param {?number} change - a calcChange() result (percent, Infinity, or null).
 * @param {Function} t - i18next translator.
 * @returns {?string} "Up 12%" / "Down 8%" / "No change" / "New", or undefined
 *   when there's no prior-period value to compare against.
 */
export function formatTrendLabel(change, t) {
  if (change == null) return undefined;
  if (change === Infinity) return t('operationsDashboard.trendNew', { defaultValue: 'New' });
  if (change === 0) return t('general.noChange');
  const direction =
    change > 0
      ? t('operationsDashboard.trendUp', { defaultValue: 'Up' })
      : t('operationsDashboard.trendDown', { defaultValue: 'Down' });
  return `${direction} ${Math.abs(Math.round(change))}%`;
}

/**
 * @param {?number} previousValue - the prior period's raw count.
 * @param {Function} t - i18next translator.
 * @returns {?string} "was {{value}}", or undefined when there's no prior value.
 */
export function formatTrendPrevious(previousValue, t) {
  if (previousValue == null) return undefined;
  return t('operationsDashboard.trendWas', { value: previousValue, defaultValue: `was ${previousValue}` });
}

export function formatSiteHours(h) {
  if (h == null || h === 0) return '0h';
  if (h < 1000) return `${Math.round(h)}h`;
  if (h < 10000) return `${(h / 1000).toFixed(1)}k h`;
  return `${Math.round(h / 1000)}k h`;
}

export function matchesSearch(row, columns, text) {
  const lower = text.toLowerCase();
  return columns.some(col => {
    const val = row[col.field];
    if (val == null) return false;
    return String(val).toLowerCase().includes(lower);
  });
}

/**
 * Windows maintenance rows to the active hours lookback: an open row is kept
 * when its `date_reported` falls inside the window, a resolved row when its
 * `date_resolved` does. Both are BE DateFields (day precision, no time of
 * day), so the comparison here matches the precision the BE's own resolved-
 * row cutoff already uses. `hours <= 0` ("all time") or `showAll` skip
 * windowing entirely — every row passes through unfiltered.
 *
 * @param {object[]} rows - maintenance notes ({status, date_reported, date_resolved, ...}).
 * @param {number} hours - lookback window in hours; <= 0 means "all time".
 * @param {boolean} showAll - user override that bypasses windowing regardless of hours.
 * @returns {object[]}
 */
export function filterMaintenanceByWindow(rows, hours, showAll) {
  if (hours <= 0 || showAll) return rows;
  const cutoff = new Date(Date.now() - hours * 3600 * 1000);
  return rows.filter(row => {
    const dateField = row.status === 'resolved' ? row.date_resolved : row.date_reported;
    const date = dateField ? new Date(dateField) : null;
    return date != null && date >= cutoff;
  });
}
